import React, { useContext, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Avatar, Button, CircularProgress, DialogContent, TextField, Typography } from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import { grey, red } from '@material-ui/core/colors';
import moment from 'moment';
import AnyDialog from '../AnyDialog';
import AppContext from '../../Services/AppContext';
import PersistantData from '../../Services/PersistantData';

const useStyles = makeStyles((theme) => ({
	content: {
		paddingTop: 0
	},
	headerRow: {
		display: 'flex',
		alignItems: 'center',
		marginBottom: theme.spacing(1)
	},
	warningIcon: {
		color: '#FEF200',
		marginRight: theme.spacing(1)
	},
	list: {
		maxHeight: 220,
		overflowY: 'auto',
		border: `1px solid ${grey[300]}`,
		borderRadius: 4,
		padding: theme.spacing(1),
		marginTop: theme.spacing(1)
	},
	groupTitle: {
		fontWeight: 700,
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(0.5)
	},
	agentRow: {
		display: 'flex',
		alignItems: 'center',
		paddingTop: theme.spacing(0.5),
		paddingBottom: theme.spacing(0.5)
	},
	avatar: {
		width: 26,
		height: 26,
		fontSize: 12,
		marginRight: theme.spacing(1),
		backgroundColor: '#FEF200',
		color: grey[900]
	},
	agentName: {
		flex: 1
	},
	agentMeta: {
		color: grey[600],
		marginLeft: theme.spacing(1)
	},
	warning: {
		color: red[500],
		marginTop: theme.spacing(2),
		display: 'block'
	},
	reason: {
		marginTop: theme.spacing(2)
	}
}));

export default function OutletInactivateDialog({ open, outletName, summary, loading, onCancel, onConfirm }) {
	const classes = useStyles();
	const [reason, setReason] = useState('');
	const [gState] = useContext(AppContext);
	const singleton = PersistantData.getInstance();
	var { translation: language } = gState;
	if (typeof (language) === 'undefined') {
		language = singleton.getlang();
	}

	useEffect(() => {
		if (open) {
			setReason('');
		}
	}, [open]);

	const appointmentRows = summary && summary.appointment ? summary.appointment : [];
	const visitRows = summary && summary.visit_schedule ? summary.visit_schedule : [];
	const totalCount = summary ? parseInt(summary.total_count, 10) : 0;
	const hasSchedule = totalCount > 0;

	const displayName = (row) => {
		if (row.name) {
			return String(row.name);
		}
		if (row.staff_code) {
			return String(row.staff_code);
		}
		return '-';
	};

	const renderGroup = (title, rows) => {
		if (rows.length === 0) {
			return null;
		}
		return (
			<>
				<Typography variant={'caption'} className={classes.groupTitle}>
					{title}
				</Typography>
				{
					rows.map((row, index) => {
						const name = displayName(row);
						return (
							<div className={classes.agentRow} key={`${title}-${index}`}>
								<Avatar className={classes.avatar}>
									{name === '-' ? '' : name.charAt(0).toUpperCase()}
								</Avatar>
								<Typography variant={'body2'} className={classes.agentName}>
									{name}
								</Typography>
								<Typography variant={'caption'}>
									{`${parseInt(row.visit_count, 10)} ${language.visits}`}
								</Typography>
								<Typography variant={'caption'} className={classes.agentMeta}>
									{`${language.next} ${moment(row.next_date, 'YYYY-MM-DD').format('DD/MM/YYYY')}`}
								</Typography>
							</div>
						);
					})
				}
			</>
		);
	};

	return (
		<AnyDialog
			open={open}
			onDismiss={onCancel}
			maxWidth={'xs'}
			title={
				<span className={classes.headerRow}>
					<WarningIcon className={classes.warningIcon} />
					<Typography variant={'h6'} component={'span'}>
						{hasSchedule ? language.outlet_tied_to_schedule : language.inactivate_outlet}
					</Typography>
				</span>
			}
			footer={() =>
				<>
					<Button onClick={onCancel} disabled={loading} color={'primary'}>
						{language.cancel}
					</Button>
					<Button
						onClick={() => onConfirm(reason.trim())}
						disabled={loading || reason.trim() === ''}
						color={'primary'}
						variant={'contained'}
						startIcon={loading ? <CircularProgress size={16} color={'inherit'} /> : null}>
						{hasSchedule ? language.inactivate_and_remove : language.inactivate}
					</Button>
				</>
			}>
			<DialogContent className={classes.content}>
				{
					hasSchedule ? (
						<>
							<Typography variant={'body2'}>
								<b>{outletName}</b> {language.outlet_scheduled_visits}
							</Typography>
							<div className={classes.list}>
								{renderGroup(language.appointment, appointmentRows)}
								{renderGroup(language.visit_schedule, visitRows)}
							</div>
							<Typography variant={'caption'} className={classes.warning}>
								{language.inactivate_remove_schedule_warning}
							</Typography>
						</>
					) : (
						<Typography variant={'body2'}>
							{language.inactivate_outlet_confirm}
						</Typography>
					)
				}
				<TextField
					className={classes.reason}
					label={language.reason_note}
					value={reason}
					onChange={(e) => setReason(e.target.value)}
					disabled={loading}
					required
					fullWidth
					multiline
					rowsMax={3}
					inputProps={{ maxLength: 255 }}
					helperText={reason.trim() === '' ? language.reason_required : ''}
				/>
			</DialogContent>
		</AnyDialog>
	);
}
